import React, { useState, useEffect } from "react";
import { generateTargetNumbers, evaluateExpression } from "../../Utils/solver24";
import { useNavigate } from "react-router-dom";
import { RotateCcw, Delete, Play, HelpCircle } from "lucide-react";

const TwentyFourGame = ({ onBack }) => {
  const navigate = useNavigate();
  const [currentNumbers, setCurrentNumbers] = useState([]);
  const [solution, setSolution] = useState(null);
  const [expression, setExpression] = useState([]);
  const [usedIndices, setUsedIndices] = useState(new Set());
  const [message, setMessage] = useState("");
  const [score, setScore] = useState(0);
  const [showSolution, setShowSolution] = useState(false);

  // Initialize game
  useEffect(() => {
    startNewRound();
  }, []);

  const startNewRound = () => {
    const { numbers, solution } = generateTargetNumbers();
    setCurrentNumbers(numbers);
    setSolution(solution);
    setExpression([]);
    setUsedIndices(new Set());
    setMessage("");
    setShowSolution(false);
  };

  const handleNumberClick = (num, index) => {
    if (usedIndices.has(index)) return;
    
    // Auto-add * if preceded by a number or )
    // Actually, forcing user to add operators is better for clarity in this game
    
    setExpression([...expression, { type: 'number', value: num, index }]);
    const newUsed = new Set(usedIndices);
    newUsed.add(index);
    setUsedIndices(newUsed);
    setMessage("");
  };

  const handleOperatorClick = (op) => {
    // Prevent starting with operator (except maybe - or ( )
    // Simplest rule: Just add it. Invalid expressions fail evaluation.
    setExpression([...expression, { type: 'operator', value: op }]);
    setMessage("");
  };

  const handleUndo = () => {
    if (expression.length === 0) return;
    const last = expression[expression.length - 1];
    
    if (last.type === 'number') {
      const newUsed = new Set(usedIndices);
      newUsed.delete(last.index);
      setUsedIndices(newUsed);
    }
    
    setExpression(expression.slice(0, -1));
    setMessage("");
  };

  const handleClear = () => {
    setExpression([]);
    setUsedIndices(new Set());
    setMessage("");
  };

  const handleCheck = () => {
    if (usedIndices.size !== 4) {
      setMessage("You must use all 4 numbers!");
      return;
    }

    const exprString = expression.map(e => e.value).join(' ');
    const result = evaluateExpression(exprString);

    if (Math.abs(result - 24) < 0.0001) {
      setMessage("Correct! 🎉");
      setScore(score + 1);
      setTimeout(startNewRound, 1500);
    } else {
      setMessage(`Incorrect. Result is ${parseFloat(result.toFixed(2))}`);
    }
  };

  const handleGiveUp = () => {
      setShowSolution(true);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h1 className="text-3xl font-bold text-gray-800">
            Make 24
            </h1>
            <div className="text-xl font-semibold text-blue-600">
                Score: {score}
            </div>
        </div>

        <p className="text-center text-gray-600 mb-8">
          Use all 4 numbers and arithmetic operators (+ - * /) to make 24.
        </p>

        {/* Display Area */}
        <div className="bg-gray-100 p-6 rounded-xl mb-8 min-h-[80px] flex items-center justify-center overflow-x-auto">
          <div className="text-3xl font-mono font-bold text-gray-800 tracking-wider">
             {expression.length === 0 ? (
                 <span className="text-gray-400 font-normal text-xl">Tap numbers and operators...</span>
             ) : (
                 expression.map((item, i) => (
                     <span key={i} className={`mx-1 ${item.type === 'operator' ? 'text-blue-600' : 'text-gray-900'}`}>
                         {item.value}
                     </span>
                 ))
             )}
          </div>
        </div>

        {/* Message Area */}
        <div className="h-8 mb-4 text-center">
            {message && (
                <span className={`font-semibold ${message.includes("Correct") ? "text-green-600" : "text-red-500"}`}>
                    {message}
                </span>
            )}
        </div>

        {/* Numbers Grid */}
        <div className="grid grid-cols-4 gap-4 mb-8">
            {currentNumbers.map((num, i) => (
                <button
                    key={i}
                    onClick={() => handleNumberClick(num, i)}
                    disabled={usedIndices.has(i)}
                    className={`
                        aspect-square rounded-xl text-3xl font-bold transition-all shadow-md
                        ${usedIndices.has(i) 
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-50 hover:scale-105"
                        }
                    `}
                >
                    {num}
                </button>
            ))}
        </div>

        {/* Operators Grid */}
        <div className="grid grid-cols-4 gap-2 mb-8 max-w-sm mx-auto">
            {['+', '-', '*', '/', '(', ')'].map((op) => (
                <button
                    key={op}
                    onClick={() => handleOperatorClick(op)}
                    className="p-3 bg-gray-50 border border-gray-300 rounded-lg text-xl font-bold text-gray-700 hover:bg-gray-100 transition-colors"
                >
                    {op}
                </button>
            ))}
            <button
                onClick={handleUndo}
                className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 hover:bg-yellow-100 transition-colors flex items-center justify-center"
                title="Undo"
            >
                <Delete size={24} />
            </button>
             <button
                onClick={handleClear}
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-colors flex items-center justify-center"
                title="Clear All"
            >
                <RotateCcw size={24} />
            </button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
            <button
                onClick={handleGiveUp}
                className="w-full sm:flex-1 py-3 bg-gray-500 text-white rounded-xl font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center whitespace-nowrap"
            >
                <HelpCircle className="mr-2 h-5 w-5" />
                Show Solution
            </button>
             <button
                onClick={handleCheck}
                className="w-full sm:flex-[2] py-3 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg flex items-center justify-center whitespace-nowrap"
            >
                <Play className="mr-2 h-5 w-5" />
                Check Answer
            </button>
            
        </div>
        
        {showSolution && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center animate-fade-in">
                <p className="text-green-800 font-medium">Possible Solution:</p>
                <p className="text-2xl font-bold text-green-900 mt-2 tracking-wider">{solution}</p>
                 <button
                    onClick={startNewRound}
                    className="mt-4 text-sm text-green-700 underline"
                 >
                     Next Puzzle
                 </button>
            </div>
        )}

      </div>
    </div>
  );
};

export default TwentyFourGame;
